import {
  useCreateWorkspaceMutation,
  useGetOrganizationQuery,
  useLazyGetWorkspacesCsvQuery,
} from '@carboncopyco/data';
import { DownloadOutlineIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  Button,
  TextPageHeader,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import { useState } from 'react';
import { usePermissionsContext } from '../../utils';
import { WorkspaceSettingsModal } from '../WorkspaceSettingsModal';
import { useWorkspacesPage } from './WorkspacesPageContext';

export const WorkspacesPageHeader = () => {
  const {
    organizationId,
    navigateToWorkspace,
    query,
    selectedSort,
    selectedAccessFilter,
    selectedTagsFilter,
    selectedTypeFilter,
  } = useWorkspacesPage();
  const { getPermissions } = usePermissionsContext();
  const [openNewWorkspaceModal, setOpenNewWorkspaceModal] = useState(false);
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });
  const createWorkspacePermission = getPermissions(
    PermissionKey.workspace,
    PermissionAction.createWorkspace
  );
  const [createWorkspace, { isLoading: isCreatingWorkspace }] =
    useCreateWorkspaceMutation();
  const { trackWorkspaceClickCreate, trackWorkspaceClickOpenCreateModal } =
    useWorkspaceAnalytics('all_workspaces_page');
  const [getDownloadCsv] = useLazyGetWorkspacesCsvQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  return (
    <div className="flex justify-center w-full">
      <div className="flex w-ds-page pt-[42px] pb-[30px] justify-between">
        <div>
          <TextPageHeader
            label={`${organization?.organizationName}'s Workspaces`}
          />
        </div>
        <div className="flex gap-2">
          {createWorkspacePermission && (
            <TooltipBubble
              placement="top"
              title="Download CSV file"
            >
              <Button
                disabled={isDownloading}
                label="CSV"
                leftIcon={<DownloadOutlineIcon size={20} />}
                secondary
                onClick={() => {
                  setIsDownloading(true);
                  getDownloadCsv({
                    organizationId,
                    sort: selectedSort,
                    query,
                    access: [...selectedAccessFilter],
                    type: [...selectedTypeFilter],
                    tags: [...selectedTagsFilter],
                  })
                    .unwrap()
                    .then((csv) => {
                      const anchor = document.createElement('a');
                      anchor.href = csv.signedUrl;
                      document.body.appendChild(anchor);
                      anchor.click();
                      document.body.removeChild(anchor);
                    })
                    .catch(() =>
                      notify.error('Report generation failed, please try again')
                    )
                    .finally(() => setIsDownloading(false));
                }}
              />
            </TooltipBubble>
          )}
          {createWorkspacePermission && (
            <Button
              leftIcon={<PlusOutlineIcon />}
              label="New workspace"
              onClick={() => {
                setOpenNewWorkspaceModal(true);
                trackWorkspaceClickOpenCreateModal();
              }}
            />
          )}
        </div>
      </div>
      <WorkspaceSettingsModal
        isNew
        isOpen={openNewWorkspaceModal}
        handleSubmit={(settings) => {
          createWorkspace({ organizationId, ...settings })
            .unwrap()
            .then((res) => {
              navigateToWorkspace(res.id);
              trackWorkspaceClickCreate({ workspace: res });
            })
            .catch(() => {
              notify.error('Error creating new workspace.');
            });
        }}
        handleClose={() => setOpenNewWorkspaceModal(false)}
        isLoading={isCreatingWorkspace}
      />
    </div>
  );
};
